import type {HeartbeatListener} from '../core/HeartbeatService';
import {HeartbeatService} from '../core/HeartbeatService';
import {SSAI_RELATED_SAMPLE_MARKER} from '../core/ssai/ssaiUtils';
import {ErrorCode} from '../enums/ErrorCode';
import {Event, EventMap} from '../enums/Event';
import type {NoExtraProperties} from '../types/NoExtraProperties';
import type {SourceMetadata} from '../types/SourceMetadata';
import {HeartbeatPayload, StateMachineCallbacks} from '../types/StateMachineCallbacks';
import {logger} from '../utils/Logger';
import * as Settings from '../utils/Settings';
import * as Utils from '../utils/Utils';
import {isNotNullish} from '../utils/Utils';

const BUFFERING_HEARTBEAT_INTERVALS = [3000, 5000, 10000, 30000, 59700];

export abstract class AnalyticsStateMachine implements HeartbeatListener {
  protected stateMachine: StateMachine.StateMachine & {[key: string]: any};
  protected onEnterStateTimestamp = 0;
  /** This boolean is set to true when the startup has succeeded, which means that the video has started playing */
  protected startupHasSucceeded = false;

  protected readonly videoStartupTimeoutMs: number = Settings.ANALYTICS_VIDEOSTART_TIMEOUT;
  protected readonly bufferTimeoutMs: number = Settings.ANALYTICS_BUFFER_TIMEOUT;
  protected readonly playingHeartbeatService: HeartbeatService;

  private bufferingHeartbeatIntervalHandle?: ReturnType<typeof setInterval>;
  private currentBufferingIntervalIndex = 0;
  private bufferingTimeoutHandle?: ReturnType<typeof setTimeout>;
  private videoStartTimeout?: ReturnType<typeof setTimeout>;

  constructor(
    protected stateMachineCallbacks: StateMachineCallbacks,
    playingHeartbeatService: HeartbeatService,
  ) {
    this.stateMachine = this.createStateMachine();
    this.playingHeartbeatService = playingHeartbeatService;
  }

  abstract createStateMachine(): StateMachine.StateMachine;

  callEvent<StateMachineEvent extends keyof EventMap, StateMachineEventData extends EventMap[StateMachineEvent]>(
    event: StateMachineEvent,
    eventData: NoExtraProperties<EventMap[StateMachineEvent], StateMachineEventData>,
    eventTimestamp: number,
  ): void {
    const stateMachineEventMethod = this.stateMachine[event];

    if (stateMachineEventMethod == null) {
      // If the event (aka. the event method) is not defined in the JSM, we log a warning,
      // because it means that we are explicitly trying to call that JSM event from our code (probably adapter),
      // but it was not defined in the JSM at all. Either we are missing it or it should not be called at all!
      logger.warn(`StateMachine ignored event "${event}" call, because it was not defined`, eventData, eventTimestamp);
      return;
    }

    try {
      // By default, if you try to call an event method not allowed in the current state, the state machine will throw an exception.
      // see: https://github.com/jakesgordon/javascript-state-machine/blob/2.4.0/README.md#handling-failures
      //
      // This JSM library behavior can be changed by providing "a custom error handler" in the `create()`
      // method of the state machine, and we do that in every state machine!
      //
      // But we still want to have here a try-catch block, in case of unexpected state machine code,
      // to avoid propagating the exception to the rest of the application (and breaking customer app).
      stateMachineEventMethod.call(this.stateMachine, eventTimestamp, eventData);
    } catch (e) {
      logger.error(
        `StateMachine ignored event "${event}" call, because it has thrown exception in the current state "${this.currentState}"`,
        eventData,
        eventTimestamp,
        e,
      );
    }
  }

  get currentState(): string {
    return this.stateMachine.current;
  }

  get hasStartupSucceeded(): boolean {
    return this.startupHasSucceeded;
  }

  resetIntervals() {
    this.clearVideoStartTimeout();
    this.clearBufferingTimeoutHandle(true);
    this.clearBufferingHeartbeatHandle();
    // explicitly call stop on playing heartbeat interval to be sure we always stop it
    // Because in case of `Event.UNLOAD` (page "unload" event or player "destroy" event) we will cancel JSM event
    // in "onBeforeEVENT" callback and no other JSM callbacks are called, in this case "onPLAYINGLeave" where we would
    // normally stop that heartbeat interval.
    this.playingHeartbeatService.stopHeartbeat();
  }

  //#region buffer timeout

  protected startBufferingHeartbeatInterval(reset = true) {
    this.resetBufferingHelpers(reset);

    this.startBufferingTimeoutHandle();

    this.bufferingHeartbeatIntervalHandle = setInterval(() => {
      const state = this.stateMachine.current.toLowerCase();
      if (state !== 'buffering') {
        this.resetBufferingHelpers();
        return;
      }
      const timestamp = new Date().getTime();
      const stateDuration = timestamp - this.onEnterStateTimestamp;
      this.stateMachineCallbacks.heartbeat(stateDuration, state, {buffered: stateDuration});
      this.onEnterStateTimestamp = timestamp;
      this.currentBufferingIntervalIndex = Math.min(
        this.currentBufferingIntervalIndex + 1,
        BUFFERING_HEARTBEAT_INTERVALS.length - 1,
      );
      this.startBufferingHeartbeatInterval(false);
    }, BUFFERING_HEARTBEAT_INTERVALS[this.currentBufferingIntervalIndex]);
  }

  protected resetBufferingHelpers(reset = true) {
    if (reset) {
      this.currentBufferingIntervalIndex = 0;
    }
    this.clearBufferingHeartbeatHandle();
    this.clearBufferingTimeoutHandle(reset);
  }

  protected clearBufferingHeartbeatHandle() {
    if (isNotNullish(this.bufferingHeartbeatIntervalHandle)) {
      clearInterval(this.bufferingHeartbeatIntervalHandle);
      this.bufferingHeartbeatIntervalHandle = undefined;
    }
  }

  private startBufferingTimeoutHandle() {
    if (this.currentBufferingIntervalIndex > 0) {
      return;
    }
    this.bufferingTimeoutHandle = setTimeout(() => {
      this.callEvent(
        Event.ERROR,
        {
          ...ErrorCode.BUFFERING_TIMEOUT_REACHED,
          data: {
            additionalData: `StateMachine timed out after ${this.bufferTimeoutMs} ms of consecutive buffering.`,
          },
        },
        Utils.getCurrentTimestamp(),
      );
      this.stateMachineCallbacks.release();
    }, this.bufferTimeoutMs);
  }

  protected clearBufferingTimeoutHandle(reset: boolean) {
    if (reset && isNotNullish(this.bufferingTimeoutHandle)) {
      clearTimeout(this.bufferingTimeoutHandle);
      this.bufferingTimeoutHandle = undefined;
    }
  }
  //#endregion

  //#region videoStartupFailed
  protected setVideoStartTimeout() {
    if (isNotNullish(this.videoStartTimeout)) {
      this.clearVideoStartTimeout();
    }
    this.videoStartTimeout = setTimeout(() => {
      this.callEvent(Event.VIDEOSTART_TIMEOUT, {}, Date.now());
      this.stateMachineCallbacks.release();
    }, this.videoStartupTimeoutMs);
  }

  protected clearVideoStartTimeout() {
    clearTimeout(this.videoStartTimeout);
    this.videoStartTimeout = undefined;
  }
  //#endregion

  onHeartbeat(playerCurrentTime: number): void {
    this.sendPlayingSample(Date.now(), playerCurrentTime);
  }

  /**
   * Only valid in the "PLAYING" state, otherwise it will log a warning and do nothing!
   *
   * It calls the {@link StateMachineCallbacks.heartbeat} callback, to send out the current playing sample (it cuts
   *  current PLAYING state) and resets time measurements to set a new start of PLAYING state again with new timestamp
   *  and video time. So the next sample will be sent with the duration from the last cut.
   *
   * We have chosen to use {@link StateMachineCallbacks.heartbeat} callback here, because it was the easiest and the
   *  fastest way to trigger 'playing' sample sending (with additional properties like "ssai marker"), without changing
   *  and refactoring too much code, in future this code should be refactored with better naming.
   *
   * @param timestamp current timestamp
   * @param playerCurrentTime the current player playback time of the video in seconds
   * @param triggeredBySsai boolean to mark the sample as SSAI triggered
   * @param triggeredByProgramChange boolean to mark the sample as ProgramChange triggered
   */
  sendPlayingSample(
    timestamp: number,
    playerCurrentTime: number,
    triggeredBySsai = false,
    triggeredByProgramChange = false,
  ) {
    const state = this.stateMachine.current.toLowerCase();
    // We don't allow this in any other state because it could interrupt the natural flow of the states.
    // Only PLAYING is considered a state which we can interrupt/cut by heartbeat or SSAI interaction.
    if (state !== 'playing') {
      // our logic for "SSAI" and "playing heartbeat" is built on the assumption that we send only 'playing' samples,
      // but that "if" restriction is spread across the codebase, so we need to log a warning here, to see a potential
      // problems in our logic, if reported by from customer and report will contain these logs
      logger.warn(`Unexpected call of "sendPlayingSample" in AnalyticsStateMachine from "${state}" state`);
      return;
    }

    this.stateMachineCallbacks.setVideoTimeEndFromEvent({currentTime: playerCurrentTime});

    const stateDuration = timestamp - this.onEnterStateTimestamp;
    const payload: HeartbeatPayload = {
      played: stateDuration,
      isProgramChange: triggeredByProgramChange ? true : undefined,
    };

    if (triggeredBySsai) {
      payload[SSAI_RELATED_SAMPLE_MARKER] = true;
    }

    this.stateMachineCallbacks.heartbeat(stateDuration, state, payload);

    this.resetCurrentStateStart(timestamp, playerCurrentTime);
  }

  private resetCurrentStateStart(stateEnterTimestamp: number, playerCurrentTime: number): void {
    this.onEnterStateTimestamp = stateEnterTimestamp;
    this.stateMachineCallbacks.setVideoTimeStartFromEvent({currentTime: playerCurrentTime});
  }

  programChange(playerCurrentTime: number, isSsaiActive: boolean, sourceMetadata: SourceMetadata): void {
    // if program change happens before or during STARTUP (or other "startup state", like bitmovin8 STARTUP_AUTOPLAY),
    // we don't want to do any "cut", because it has not started yet, we just want to update source metadata
    // and wait for the natural start
    if (
      this.stateMachine.current === 'SETUP' ||
      this.stateMachine.current === 'READY' ||
      this.stateMachine.current === 'STARTUP' ||
      this.stateMachine.current === 'STARTUP_AUTOPLAY'
    ) {
      this.stateMachineCallbacks.updateSourceMetadata(sourceMetadata);
      return;
    }

    const programChangeTimestamp = Date.now();

    // If the program change happens during PLAYING state, we want to "cut it" and send asap last playing sample
    // of the previous program (session) with the duration from the start of PLAYING state to the program change
    if (this.stateMachine.current === 'PLAYING') {
      this.sendPlayingSample(programChangeTimestamp, playerCurrentTime, isSsaiActive, true);
    }
    // ... else in any other state, we just want to reset time measurements to set a new start of the current state
    else {
      this.resetCurrentStateStart(programChangeTimestamp, playerCurrentTime);
    }

    this.stateMachineCallbacks.programChange(sourceMetadata);
  }
}
